import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { Wrench, Plus, Edit, Trash2, Search, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

const DefeitosPage = () => {
    const [defeitos, setDefeitos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingDefeito, setEditingDefeito] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const { hasRole } = useAuth();

    const [formData, setFormData] = useState({
        nome: '',
        descricao: '',
        ativo: true
    });

    useEffect(() => {
        fetchDefeitos();
    }, []);

    const fetchDefeitos = async () => {
        try {
            const response = await axios.get(`${API}/defeitos`);
            setDefeitos(response.data);
        } catch (error) {
            toast.error('Erro ao carregar defeitos');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            if (editingDefeito) {
                await axios.put(`${API}/defeitos/${editingDefeito.id}`, formData);
                toast.success('Defeito atualizado!');
            } else {
                await axios.post(`${API}/defeitos`, formData);
                toast.success('Defeito cadastrado!');
            }
            setDialogOpen(false);
            resetForm();
            fetchDefeitos();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao salvar defeito');
        }
    };

    const handleEdit = (defeito) => {
        setEditingDefeito(defeito);
        setFormData({
            nome: defeito.nome,
            descricao: defeito.descricao || '',
            ativo: defeito.ativo
        });
        setDialogOpen(true);
    };

    const handleDelete = async (defeito) => {
        if (!window.confirm(`Deseja remover o defeito "${defeito.nome}"?`)) return;
        
        try {
            await axios.delete(`${API}/defeitos/${defeito.id}`);
            toast.success('Defeito removido!');
            fetchDefeitos();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao remover defeito');
        }
    };

    const resetForm = () => {
        setFormData({ nome: '', descricao: '', ativo: true });
        setEditingDefeito(null);
    };

    const canEdit = hasRole(['admin', 'gestor']);
    const canDelete = hasRole(['admin']);

    // Filtrar defeitos com base na busca
    const filteredDefeitos = defeitos.filter(defeito => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
            defeito.nome?.toLowerCase().includes(term) ||
            defeito.descricao?.toLowerCase().includes(term)
        );
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-cyan-400">Carregando...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="defeitos-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-white tracking-tight">
                        Cadastro de Defeitos
                    </h1>
                    <p className="text-sm text-slate-400 mt-1">
                        Gerenciamento de tipos de defeitos
                    </p>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
                    {/* Campo de Busca */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Buscar defeitos..."
                            className="pl-10 pr-8 w-full sm:w-64 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
                            data-testid="search-defeitos"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                                data-testid="clear-search-defeitos"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                
                    {canEdit && (
                        <Dialog open={dialogOpen} onOpenChange={(open) => {
                            setDialogOpen(open);
                            if (!open) resetForm();
                        }}>
                            <DialogTrigger asChild>
                                <Button 
                                    data-testid="btn-new-defeito"
                                    className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Novo Defeito
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-md">
                                <DialogHeader>
                                    <DialogTitle className="font-heading text-xl text-white">
                                        {editingDefeito ? 'Editar Defeito' : 'Novo Defeito'}
                                    </DialogTitle>
                                </DialogHeader>
                            
                                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Nome *
                                        </Label>
                                        <Input
                                        data-testid="input-defeito-nome"
                                        value={formData.nome}
                                        onChange={(e) => setFormData({...formData, nome: e.target.value})}
                                        required
                                        placeholder="Ex: Falta de manutenção"
                                        className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                        Descrição
                                    </Label>
                                    <Textarea
                                        value={formData.descricao}
                                        onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                                        placeholder="Descrição detalhada do defeito..."
                                        className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm min-h-[80px]"
                                    />
                                </div>

                                <div className="flex items-center justify-between">
                                    <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                        Ativo
                                    </Label>
                                    <Switch
                                        checked={formData.ativo}
                                        onCheckedChange={(checked) => setFormData({...formData, ativo: checked})}
                                    />
                                </div>

                                <div className="flex gap-3 pt-4">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => {
                                            setDialogOpen(false);
                                            resetForm();
                                        }}
                                        className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm"
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        type="submit"
                                        data-testid="btn-save-defeito"
                                        className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm"
                                    >
                                        {editingDefeito ? 'Atualizar' : 'Cadastrar'}
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
                </div>
            </div>

            {/* Table */}
            <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                <CardContent className="p-0">
                    {filteredDefeitos.length === 0 ? (
                        <div className="p-12 text-center">
                            <Wrench className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                            <p className="text-slate-400">
                                {searchTerm ? 'Nenhum defeito encontrado para a busca' : 'Nenhum defeito cadastrado'}
                            </p>
                        </div>
                    ) : (
                        <Table className="table-naval">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Nome</TableHead>
                                    <TableHead>Descrição</TableHead>
                                    <TableHead>Status</TableHead>
                                    {canEdit && <TableHead>Ações</TableHead>}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredDefeitos.map((defeito) => (
                                    <TableRow key={defeito.id} data-testid={`defeito-row-${defeito.id}`}>
                                        <TableCell className="text-white font-medium">
                                            {defeito.nome}
                                        </TableCell>
                                        <TableCell className="text-slate-400">
                                            {defeito.descricao || '-'}
                                        </TableCell>
                                        <TableCell>
                                            <span className={`
                                                px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm
                                                ${defeito.ativo ? 'badge-success' : 'badge-danger'}
                                            `}>
                                                {defeito.ativo ? 'Ativo' : 'Inativo'}
                                            </span>
                                        </TableCell>
                                        {canEdit && (
                                            <TableCell>
                                                <div className="flex gap-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleEdit(defeito)}
                                                        className="text-slate-400 hover:text-white hover:bg-slate-800"
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </Button>
                                                    {canDelete && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleDelete(defeito)}
                                                            className="text-red-400 hover:text-red-300 hover:bg-slate-800"
                                                            data-testid={`btn-delete-defeito-${defeito.id}`}
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default DefeitosPage;
